import { type Event, type EventParticipant, isRegisteredEventParticipant } from 'models';
import { useCallback } from 'react';
import { Avatar, AvatarWrapper, Card } from 'ui';
import { useUser } from 'features/auth';
import { IconDirectMessageButtonNode } from 'features/chat';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import { UserLink, UserPopover } from 'features/users/components';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import {
  useGetNodeUserAvatar,
  useGetUserFirstNameWithFallback,
  useGetUserLastNameWithFallback,
  useGetUserNameWithFallback,
} from 'features/users/hooks';
import { useGetInviteesBadgeIcon } from '../../../../components';

type P = {
  event: Event;
};

const EventInvitees = ({ event }: P) => {
  const { latestConfirmedParticipants, participants } = event;
  const { nodeUserData: nodeViewer } = useUser();
  const getAvatarUrl = useGetNodeUserAvatar();
  const getUserFirstNameWithFallback = useGetUserFirstNameWithFallback();
  const getUserLastNameWithFallback = useGetUserLastNameWithFallback();
  const getUserNameWithFallback = useGetUserNameWithFallback();
  const getBadgeIcon = useGetInviteesBadgeIcon();

  const getPatientAvatarUrl = useCallback(
    (patient: EventParticipant) =>
      isRegisteredEventParticipant(patient) ? getAvatarUrl({ size: 'medium', userId: patient.ref }) : undefined,
    [getAvatarUrl],
  );

  const latestConfirmedInvitees = (latestConfirmedParticipants ?? [])
    .slice(0, 10)
    .flatMap((participantId) => participants.find((participant) => participant.ref === participantId) ?? []);

  if (latestConfirmedInvitees.length === 0) {
    return null;
  }

  return (
    <Card.HorizontalScroll
      items={latestConfirmedInvitees.map((patient) => ({
        content: isRegisteredEventParticipant(patient) ? (
          <UserPopover
            fallback={{ ...patient, profilePhoto: getPatientAvatarUrl(patient) }}
            popoverProps={{ stretch: true }}
            userId={patient.ref}
          >
            <UserLink userId={patient.ref}>
              <AvatarWrapper
                avatar={{
                  badge: {
                    icon: getBadgeIcon({ rsvp: patient.rsvp }),
                  },
                  photo: getPatientAvatarUrl(patient),
                }}
                extra={
                  patient.email ? (
                    <IconDirectMessageButtonNode
                      user={{
                        _id: patient.ref,
                        email: patient.email,
                        firstName: getUserFirstNameWithFallback(patient),
                        lastName: getUserLastNameWithFallback(patient),
                      }}
                      viewer={nodeViewer}
                    />
                  ) : null
                }
                user={{ isViewer: patient.ref === nodeViewer._id, name: getUserNameWithFallback(patient) }}
              />
            </UserLink>
          </UserPopover>
        ) : (
          <Avatar badge={{ icon: getBadgeIcon({ rsvp: patient.rsvp }) }} size={32} />
        ),
        key: patient.ref ?? patient.email,
      }))}
      title="Latest confirmed invitees"
    />
  );
};

export default EventInvitees;
